import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Card,
  CardContent,
  Grid,
  TextField,
  Button,
  Alert,
  Typography,
  Divider,
  Paper,
  Chip,
  CircularProgress,
  Tooltip,
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import RefreshIcon from "@mui/icons-material/Refresh";
import DeleteSweepIcon from "@mui/icons-material/DeleteSweep";

import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import MDTypography from "../../../components/MDTypography";
import { sheetsApi } from "../../../services/api/omni/sheets";

const SERVICE = "omni";
const NAME = "shoppingtable";
const CACHE_KEY = `sheet_settings_${SERVICE}_${NAME}`;
const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes

/** All shopping-table fields with human-readable labels and hints */
const COLUMN_FIELDS = [
  { key: "datum", label: "Datum", hint: "Date of purchase, e.g. A" },
  { key: "name", label: "Name", hint: "Article name column, e.g. B" },
  { key: "ek_ort", label: "EK-Ort", hint: "Purchase location column" },
  { key: "angekommen", label: "Angekommen", hint: "Arrived? (Ja/Nein) column" },
  {
    key: "eingebucht",
    label: "Eingebucht",
    hint: "Booked in? (Ja/Nein) column",
  },
  { key: "einheiten", label: "Einheiten", hint: "Number of units column" },
  { key: "preis", label: "Preis", hint: "Total price column" },
  {
    key: "preis_pro_einheit_b",
    label: "Preis/Einheit B",
    hint: "Brutto price per unit column",
  },
  {
    key: "preis_pro_einheit_n",
    label: "Preis/Einheit N",
    hint: "Netto price per unit column",
  },
  {
    key: "bestellnummern",
    label: "Bestellnummern",
    hint: "Order numbers (comma-separated) column",
  },
  {
    key: "einschicken",
    label: "Einschicken",
    hint: "'multi' or 'amazon' column",
  },
  { key: "asin_sku", label: "ASIN / SKU", hint: "ASIN or SKU column" },
  { key: "amazon", label: "Amazon", hint: "Amazon column" },
  {
    key: "freischaltung",
    label: "Freischaltung",
    hint: "Activation status column",
  },
  { key: "rechnung", label: "Rechnung", hint: "Invoice column" },
  {
    key: "sendungsnummer",
    label: "Sendungsnummer",
    hint: "Tracking number column",
  },
  { key: "hersteller", label: "Hersteller", hint: "Manufacturer column" },
];

const EMPTY_MAPPING = Object.fromEntries(COLUMN_FIELDS.map((f) => [f.key, ""]));

const extractIdsFromUrl = (url) => {
  try {
    const spreadsheetMatch = url.match(/\/spreadsheets\/d\/([a-zA-Z0-9-_]+)/);
    const gidMatch = url.match(/[?&]gid=(\d+)/);
    return {
      spreadsheetId: spreadsheetMatch?.[1] ?? "",
      sheetId: gidMatch?.[1] ?? "",
    };
  } catch {
    return { spreadsheetId: "", sheetId: "" };
  }
};

const SheetSettingsTab = () => {
  const [settingsId, setSettingsId] = useState(null);
  const [spreadsheetId, setSpreadsheetId] = useState("");
  const [sheetId, setSheetId] = useState("");
  const [version, setVersion] = useState("v4");
  const [columnMapping, setColumnMapping] = useState(EMPTY_MAPPING);
  const [sheetsUrl, setSheetsUrl] = useState("");
  const [urlError, setUrlError] = useState("");

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const applySettings = (s) => {
    setSettingsId(s.id ?? null);
    setSpreadsheetId(s.spreadsheet_id ?? "");
    setSheetId(s.sheet_id ?? "");
    setVersion(s.version ?? "v4");
    setColumnMapping({ ...EMPTY_MAPPING, ...(s.column_mapping ?? {}) });
  };

  const writeCache = (s) => {
    try {
      localStorage.setItem(
        CACHE_KEY,
        JSON.stringify({ data: s, ts: Date.now() }),
      );
    } catch {
      /* storage full / private mode */
    }
  };

  const loadSettings = useCallback(async (forceRefresh = false) => {
    // Try localStorage first
    if (!forceRefresh) {
      try {
        const cached = localStorage.getItem(CACHE_KEY);
        if (cached) {
          const { data, ts } = JSON.parse(cached);
          if (Date.now() - ts < CACHE_TTL_MS) {
            applySettings(data);
            return;
          }
        }
      } catch {
        /* corrupt cache – fall through */
      }
    }

    setLoading(true);
    setError(null);
    try {
      const res = await sheetsApi.getSettingsByServiceAndName(SERVICE, NAME);
      applySettings(res.data);
      writeCache(res.data);
    } catch (e) {
      if (e.response?.status !== 404) {
        setError("Failed to load sheet settings.");
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  const handleUrlChange = (url) => {
    setSheetsUrl(url);
    setUrlError("");
    if (!url.trim()) return;
    if (!url.includes("docs.google.com/spreadsheets")) {
      setUrlError("Not a valid Google Sheets URL");
      return;
    }
    const { spreadsheetId: sid, sheetId: gid } = extractIdsFromUrl(url);
    if (sid) setSpreadsheetId(sid);
    if (gid) setSheetId(gid);
  };

  const handleColumnChange = (key, value) => {
    const upper = value.toUpperCase();
    if (upper === "" || /^[A-Z]{1,3}$/.test(upper)) {
      setColumnMapping((prev) => ({ ...prev, [key]: upper }));
    }
  };

  const handleSave = async () => {
    if (!spreadsheetId.trim()) {
      setError("Spreadsheet ID is required.");
      return;
    }
    if (!sheetId.trim()) {
      setError("Sheet ID (gid) is required.");
      return;
    }
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      // Strip empty values from mapping before saving
      const cleanMapping = Object.fromEntries(
        Object.entries(columnMapping).filter(([, v]) => v.trim() !== ""),
      );
      await sheetsApi.saveSettings({
        name: NAME,
        service: SERVICE,
        spreadsheet_id: spreadsheetId.trim(),
        sheet_id: sheetId.trim(),
        version,
        column_mapping: cleanMapping,
      });
      setSuccess("Settings saved successfully.");
      await loadSettings(true); // force refresh to get server-assigned id
    } catch (e) {
      setError(e.response?.data?.detail ?? "Failed to save settings.");
    } finally {
      setSaving(false);
    }
  };

  const handleResetMapping = async () => {
    if (!settingsId) {
      setError("Save settings first before resetting the mapping.");
      return;
    }
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      await sheetsApi.resetColumnMapping(settingsId);
      setColumnMapping(EMPTY_MAPPING);
      // Invalidate cache so next load reflects cleared mapping
      try {
        localStorage.removeItem(CACHE_KEY);
      } catch {}
      setSuccess("Column mapping reset.");
    } catch (e) {
      setError(e.response?.data?.detail ?? "Failed to reset mapping.");
    } finally {
      setSaving(false);
    }
  };

  const inputSx = {
    "& .MuiInputBase-input": { color: "white" },
    "& .MuiInputLabel-root": { color: "rgba(255,255,255,0.7)" },
    "& .MuiOutlinedInput-root": {
      "& fieldset": { borderColor: "rgba(255,255,255,0.3)" },
      "&:hover fieldset": { borderColor: "rgba(255,255,255,0.6)" },
      "&.Mui-focused fieldset": { borderColor: "white" },
    },
    "& .MuiFormHelperText-root": { color: "rgba(255,255,255,0.6)" },
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" mt={6}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert
          severity="success"
          sx={{ mb: 2 }}
          onClose={() => setSuccess(null)}
        >
          {success}
        </Alert>
      )}

      {/* ── Spreadsheet Configuration ── */}
      <Card sx={{ backgroundColor: "#0F9D58", color: "white", mb: 3 }}>
        <CardContent>
          <MDTypography
            variant="h6"
            fontWeight="medium"
            sx={{ color: "white", mb: 2 }}
          >
            Google Spreadsheet Configuration
          </MDTypography>

          {/* URL parser */}
          <Paper
            sx={{ p: 2, mb: 2, backgroundColor: "rgba(255,255,255,0.12)" }}
          >
            <MDTypography
              variant="body2"
              sx={{ color: "white", mb: 1, fontWeight: "medium" }}
            >
              Paste Spreadsheet URL (auto-parse)
            </MDTypography>
            <TextField
              fullWidth
              size="small"
              label="Google Sheets URL"
              placeholder="https://docs.google.com/spreadsheets/d/…/edit?gid=…"
              value={sheetsUrl}
              onChange={(e) => handleUrlChange(e.target.value)}
              error={!!urlError}
              helperText={
                urlError ||
                "Spreadsheet ID and Sheet ID will be extracted automatically"
              }
              sx={inputSx}
            />
          </Paper>

          {/* Manual IDs */}
          <Grid container spacing={2}>
            <Grid item xs={12} md={7}>
              <TextField
                fullWidth
                size="small"
                label="Spreadsheet ID"
                placeholder="1TSodLjztKpEgCWMZdfaktzJSdVXKUUgzHMIcPfsB…"
                value={spreadsheetId}
                onChange={(e) => setSpreadsheetId(e.target.value)}
                sx={inputSx}
              />
            </Grid>
            <Grid item xs={8} md={3}>
              <TextField
                fullWidth
                size="small"
                label="Sheet ID (gid)"
                placeholder="0"
                value={sheetId}
                onChange={(e) => setSheetId(e.target.value)}
                sx={inputSx}
              />
            </Grid>
            <Grid item xs={4} md={2}>
              <TextField
                fullWidth
                size="small"
                label="API Version"
                value={version}
                onChange={(e) => setVersion(e.target.value)}
                sx={inputSx}
              />
            </Grid>
          </Grid>

          {/* Live link preview */}
          {spreadsheetId && (
            <Box sx={{ mt: 1.5 }}>
              <Typography
                variant="caption"
                sx={{ color: "rgba(255,255,255,0.7)" }}
              >
                <a
                  href={`https://docs.google.com/spreadsheets/d/${spreadsheetId}/edit${
                    sheetId ? `?gid=${sheetId}` : ""
                  }`}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: "inherit" }}
                >
                  Open Spreadsheet ↗
                </a>
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* ── Column Mapping ── */}
      <Card sx={{ backgroundColor: "#1a73e8", color: "white", mb: 3 }}>
        <CardContent>
          <Box display="flex" alignItems="center" gap={1} mb={2}>
            <MDTypography
              variant="h6"
              fontWeight="medium"
              sx={{ color: "white" }}
            >
              Column Mapping
            </MDTypography>
            <Tooltip title="Enter the spreadsheet column letter (A–ZZ) for each field. Leave empty to ignore a field.">
              <InfoOutlinedIcon
                sx={{ color: "rgba(255,255,255,0.7)", fontSize: 18 }}
              />
            </Tooltip>
          </Box>

          <Grid container spacing={1.5}>
            {COLUMN_FIELDS.map(({ key, label, hint }) => (
              <Grid item xs={6} sm={4} md={3} key={key}>
                <TextField
                  fullWidth
                  size="small"
                  label={label}
                  placeholder="e.g. A"
                  value={columnMapping[key] ?? ""}
                  onChange={(e) => handleColumnChange(key, e.target.value)}
                  helperText={hint}
                  inputProps={{
                    maxLength: 3,
                    style: { textTransform: "uppercase" },
                  }}
                  sx={inputSx}
                />
              </Grid>
            ))}
          </Grid>

          {/* Mapping summary chips */}
          <Box sx={{ mt: 2, display: "flex", flexWrap: "wrap", gap: 0.8 }}>
            {COLUMN_FIELDS.filter((f) => columnMapping[f.key]).map((f) => (
              <Chip
                key={f.key}
                label={`${f.label}: ${columnMapping[f.key]}`}
                size="small"
                sx={{
                  backgroundColor: "rgba(255,255,255,0.2)",
                  color: "white",
                }}
              />
            ))}
            {!COLUMN_FIELDS.some((f) => columnMapping[f.key]) && (
              <Typography
                variant="caption"
                sx={{ color: "rgba(255,255,255,0.5)" }}
              >
                No columns mapped yet.
              </Typography>
            )}
          </Box>
        </CardContent>
      </Card>

      {/* ── Action Buttons ── */}
      <Box display="flex" flexWrap="wrap" gap={2} mb={3}>
        <Button
          variant="contained"
          startIcon={
            saving ? (
              <CircularProgress size={16} color="inherit" />
            ) : (
              <SaveIcon />
            )
          }
          onClick={handleSave}
          disabled={saving}
          sx={{
            backgroundColor: "#0F9D58",
            "&:hover": { backgroundColor: "#0b7a44" },
          }}
        >
          {saving ? "Saving…" : "Save Settings"}
        </Button>

        <Button
          variant="outlined"
          startIcon={<DeleteSweepIcon />}
          onClick={handleResetMapping}
          disabled={saving || !settingsId}
          sx={{
            color: "#EA4335",
            borderColor: "#EA4335",
            "&:hover": { backgroundColor: "rgba(234,67,53,0.08)" },
          }}
        >
          Reset Column Mapping
        </Button>

        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={() => loadSettings(true)}
          disabled={loading}
          sx={{ color: "#fff", borderColor: "rgba(255,255,255,0.4)" }}
        >
          Reload
        </Button>
      </Box>

      <Divider sx={{ mb: 3 }} />

      <Alert severity="info">
        <strong>Column mapping guide:</strong> Enter the letter of the column in
        your Google Sheet that corresponds to each field (e.g. <code>A</code>,{" "}
        <code>B</code>, <code>AA</code>). The first row is treated as a header
        and skipped automatically. Unmapped fields will be left empty when
        importing rows.
      </Alert>
    </Box>
  );
};

export default SheetSettingsTab;
